import "./autodeskMap.scss";

import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { debounce } from "throttle-debounce";
import useDeepCompareEffect from "use-deep-compare-effect";
import useResizeObserver from '@react-hook/resize-observer'

import { AutodeskConstants } from "../autodeskConstants";
import { EndpointContextBuilder } from "../builders/endpointContextBuilder";
import { IClickEvent, IMarker, ISelectorEvent } from "../interfaces";
import { IAutodeskMapProps } from "../interfaces/IAutodeskMapProps";
import { IMapToolbar } from "../interfaces/IMapToolbar";
import { IViewerFloor } from "../interfaces/IViewerFloor";
import { IViewerVenue } from "../interfaces/IViewerVenue";
import { ISpaceOnMap } from "../interfaces/spaces";
import { MapToolbarDefaults } from "../mapToolbarDefaults";
import { Point } from "../models/point";
import { ViewerFloorsDictionary } from "../models/viewerFloorsDictionary";
import { CameraUtils } from "../utils/cameraUtils";
import { LabelManager } from "../utils/labelManager";
import { MarkerManager } from "../utils/markerManager";
import { StyleManager } from "../utils/styleManager";
import { WebGlUtils } from "../utils/webGLUtils";
import { ZoomManager } from "../utils/zoomManager";
import { ZoomUtils } from "../utils/zoomUtils";
import { AutodeskContext } from "./autodeskContext";
import { AutodeskViewer } from "./autodeskViewer";
import { CameraContext, ICameraRotation } from "./extensions/cameraExtension";
import { Extensions } from "./extensions/extensions";
import { FloorSelectionExtensionOptions } from "./extensions/floorSelector/floorSelectionExtensionOptions";
import { SelectorExtensionContext } from "./extensions/selectorExtension";
import { ViewControlContext } from "./extensions/viewControlExtension";

enum MapStatus {
    mapUnInitialized = 0,
    mapInitialized = 2,
    venueInitialized = 3,
    floorsLoaded = 4,
    floorLoaded = 5,
}

interface ViewerStatus {
    mapStatus: MapStatus;
}

/**
 * Cleanup Autodesk maps (free used memory)
 *
 * @remark Only use this when no instances of the AutodeskMap component are mounted!
 *
 * @see https://forge.autodesk.com/en/docs/viewer/v7/reference/#shutdown
 */
export const cleanupAutodeskMaps = () => {
    Autodesk.Viewing?.shutdown();
};

/**
 *
 * @param props Autodesk component properties
 * @returns Autodesk component
 *
 * @remarks in some cases useDeepCompareEffect is used instead of useEffect, because the dependency array
 *   contains an object or array. (https://www.npmjs.com/package/use-deep-compare-effect)
 */
export const AutodeskMap: React.FC<IAutodeskMapProps> = (props) => {
    const viewerReference: React.RefObject<HTMLDivElement> = useRef(null);
    const extensions: React.RefObject<Extensions> = useRef(new Extensions(props.id));
    const autodeskViewer = useRef(new AutodeskViewer());
    const autodeskContext = useRef(new AutodeskContext());
    const [selectorExtensionContext] = useState<SelectorExtensionContext>(new SelectorExtensionContext());
    const [cameraExtensionContext] = useState<CameraContext>(new CameraContext());
    const [viewExtensionContext] = useState<ViewControlContext>(new ViewControlContext());
    const floorSelectorExtensionOptionsReference = useRef<FloorSelectionExtensionOptions>();
    const endpointContextBuilder = useRef(EndpointContextBuilder.Instance);
    const markerManagerReference = useRef<MarkerManager>();
    const styleManagerReference = useRef(new StyleManager(autodeskViewer.current));
    const labelManagerReference = useRef(new LabelManager(autodeskViewer.current, props.showLabelsAtZoomLevel, props.hideOverlappingLabels));
    const zoomManagerReference = useRef(new ZoomManager(autodeskViewer.current));
    const [status, setStatus] = useState<ViewerStatus>({ mapStatus: MapStatus.mapUnInitialized });
    const [spaces, setSpaces] = useState<ISpaceOnMap[] | undefined>(undefined);
    const { onPlaceClickAsync, onClickAsync, onRightClickAsync, onFloorChangedAsync, onBeforeFloorChangeAsync, onZoomChange } = props;

    // Force resize the viewer whenever our container resizes
    useResizeObserver(viewerReference, (entry) => { autodeskViewer.current.viewer.resize(); });

    const refreshMarkers = useCallback(
        (currentMarkers: IMarker[], payload: { markersUpdate: IMarker[]; floorLevel: number | undefined }): IMarker[] => {
            if (markerManagerReference.current == null) {
                return [];
            }
            return markerManagerReference.current.updateMarkers(payload);
        },
        []
    );

    const [nonSpaceMarkers, setNonSpaceMarkers] = useReducer(refreshMarkers, []);

    /*
     * Store spaces in state and set to undefined when changing venue.
     * This avoids triggering style, camera effects using spaces from previous venues
     */
    useEffect(() => {
        setSpaces(props.spaces);
    }, [props.spaces]);

    /*
     * Window/document resize
     */
    useEffect(() => {
        if (status.mapStatus === MapStatus.mapUnInitialized || !props.resize) {
            return;
        }
        autodeskViewer.current.viewer.resize();
    }, [status.mapStatus, props.resize]);

    // Load or update the toolbar extension when the map has initialized or the toolbar has changed
    // Updating the settings does not work as the extension is not re-added or its state updated
    useEffect(() => {
        if (status.mapStatus !== MapStatus.mapInitialized || !props.toolbar) {
            return;
        }

        const autodeskViewerRef = autodeskViewer.current;

        const handleToolbarExtensionAsync = async (toolbarSettings: IMapToolbar): Promise<void> => {
            await extensions.current?.loadToolbarExtensionAsync(autodeskViewerRef.viewer, toolbarSettings);
        };

        const toolbarSettings = { ...MapToolbarDefaults.instance, ...props.toolbar };

        handleToolbarExtensionAsync(toolbarSettings);
    }, [status.mapStatus, props.toolbar]);

    /**
     * Zoom Control
     */
    useEffect(() => {
        if (status.mapStatus !== MapStatus.mapInitialized) {
            return;
        }
        const autodeskViewerRef = autodeskViewer.current;

        const loadViewControlExtensionAsync = async (viewer: Autodesk.Viewing.GuiViewer3D, toolbarSettings: IMapToolbar): Promise<void> => {
            await extensions.current?.loadViewControlExtensionAsync(viewer, {
                autodeskContainerId: props.id,
                showZoomButtons: toolbarSettings.showZoomButtons,
                showRotateButtons: toolbarSettings.showRotateButtons,
                degreesPerClick: toolbarSettings.degreesPerClick,
                showForceLabelsButton: toolbarSettings.showForceLabelsButton,
                showLabelsOnZoomButton: toolbarSettings.showLabelsOnZoomButton,
                viewControlContext: viewExtensionContext
            });
        };

        const toolbarSettings = { ...MapToolbarDefaults.instance, ...props.toolbar };

        loadViewControlExtensionAsync(autodeskViewerRef.viewer, toolbarSettings);
    }, [props.id, status.mapStatus]);

    /**
     * Camera
     */
    useEffect(() => {
        if (status.mapStatus < MapStatus.floorLoaded || !spaces) {
            return;
        }

        const autodeskViewerRef = autodeskViewer.current;

        const onDebounceCameraChanged = (rotation?: ICameraRotation) => {

            if (props.redrawMarkersOnCameraChange) {
                const markers = props.markers || [];
                const spaceMarkers = markerManagerReference.current!.getSpaceMarkers(spaces ?? []);
                const allMarkers = markers.concat(spaceMarkers);
                setNonSpaceMarkers({ markersUpdate: allMarkers, floorLevel: autodeskContext.current.activeFloorLevel });
            }

            labelManagerReference.current.cleanLabels();
            labelManagerReference.current.applyLabels(spaces, viewExtensionContext.forceLabels, viewExtensionContext.showLabelsOnZoom);
        };

        const loadCameraExtensionAsync = async (viewer: Autodesk.Viewing.GuiViewer3D): Promise<void> => {
            await extensions.current?.loadCameraExtensionAsync(viewer, {
                onDebounceCameraChanged: onDebounceCameraChanged,
                cameraContext: cameraExtensionContext,
                keepCameraInitialPosition: props.keepCameraInitialPosition,
            });
        };

        loadCameraExtensionAsync(autodeskViewerRef.viewer);
    }, [status.mapStatus, spaces, props.keepCameraInitialPosition, cameraExtensionContext]);

    /**
     * Background color
     */
    useEffect(() => {
        if (status.mapStatus !== MapStatus.mapInitialized || !props.backgroundColor) {
            return;
        }
        const backgroundColor = props.backgroundColor;

        autodeskViewer.current.viewer.setBackgroundColor(
            backgroundColor.red,
            backgroundColor.green,
            backgroundColor.blue,
            backgroundColor.gradientRed ?? backgroundColor.red,
            backgroundColor.gradientGreen ?? backgroundColor.green,
            backgroundColor.gradientBlue ?? backgroundColor.blue
        );
    }, [status.mapStatus, props.backgroundColor]);

    /**
     * Zoom
     */
    useEffect(() => {
        if (status.mapStatus !== MapStatus.floorLoaded || !props.zoom) {
            return;
        }
        const viewer = autodeskViewer.current.viewer;

        const autodeskContextRef = autodeskContext.current;

        if (ZoomUtils.isZoomOnSpace(props.zoom.zoomInOn)) {
            ZoomUtils.zoomInOnSpace(props.zoom.zoomInOn, viewer);
        } else if (ZoomUtils.isZoomOnPosition(props.zoom.zoomInOn)) {
            ZoomUtils.zoomInOnPosition(props.zoom.zoomInOn, viewer);
        } else if (ZoomUtils.isZoomOnXY(props.zoom.zoomInOn)) {
            ZoomUtils.zoomInOnXY(props.zoom.zoomInOn, viewer);
        } else if (ZoomUtils.isIgnoreZoom(props.zoom.zoomInOn)) {
            if (markerManagerReference.current?.initialCameraPosition) {
                ZoomUtils.zoomToInitial(markerManagerReference.current.initialCameraPosition, viewer);
            }
        }

        const markers = props.markers || [];
        const spaceMarkers = markerManagerReference.current!.getSpaceMarkers(spaces ?? []);
        const allMarkers = markers.concat(spaceMarkers);
        setNonSpaceMarkers({ markersUpdate: allMarkers, floorLevel: autodeskContextRef.activeFloorLevel });

        labelManagerReference.current.cleanLabels();
        labelManagerReference.current.applyLabels(spaces || [], viewExtensionContext.forceLabels, viewExtensionContext.showLabelsOnZoom);

    }, [status.mapStatus, JSON.stringify(props.zoom)]);

    /*
     * Show (space)styles
     */
    useDeepCompareEffect(() => {
        if (status.mapStatus !== MapStatus.floorLoaded || spaces == null) {
            return;
        }

        const autodeskContextRef = autodeskContext.current;

        const applyStylesLabelsAndMarkers = async (spaces: ISpaceOnMap[]) => {
            styleManagerReference.current.cleanStyles();
            labelManagerReference.current.cleanLabels();
            styleManagerReference.current.applyStyles(autodeskContextRef.model!, spaces);
            labelManagerReference.current.applyLabels(spaces, viewExtensionContext.forceLabels, viewExtensionContext.showLabelsOnZoom);

            const markers = props.markers || [];
            const spaceMarkers = markerManagerReference.current!.getSpaceMarkers(spaces ?? []);
            const allMarkers = markers.concat(spaceMarkers);
            setNonSpaceMarkers({ markersUpdate: allMarkers, floorLevel: autodeskContextRef.activeFloorLevel });
        };

        applyStylesLabelsAndMarkers(spaces ?? []);
    }, [status.mapStatus, spaces ?? []]);

    useEffect(() => {
        if (autodeskViewer.current.viewer && props.rotateDegrees) {
            CameraUtils.rotate(autodeskViewer.current.viewer, props.rotateDegrees!);
        }

    }, [props.rotateDegrees]);

    // Redraw markers when props.markers change
    useEffect(() => {
        if (status.mapStatus !== MapStatus.floorLoaded || !props.markers) {
            return;
        }

        const autodeskContextRef = autodeskContext.current;

        // Redraw ALL markers
        const markers = props.markers;
        const spaceMarkers = markerManagerReference.current!.getSpaceMarkers(spaces ?? []);
        const allMarkers = markers.concat(spaceMarkers);

        setNonSpaceMarkers({ markersUpdate: allMarkers, floorLevel: autodeskContextRef.activeFloorLevel });

    }, [status.mapStatus, JSON.stringify(props.markers)]);

    /*
     * Show style for space selection
     */
    useDeepCompareEffect(() => {
        if (status.mapStatus !== MapStatus.floorLoaded || !props.selectedSpaces) {
            return;
        }
        styleManagerReference.current.cleanStyles();
        labelManagerReference.current.cleanLabels();
        styleManagerReference.current.applyStyles(autodeskContext.current.model!, props.selectedSpaces);
        labelManagerReference.current.applyLabels(props.selectedSpaces, viewExtensionContext.forceLabels, viewExtensionContext.showLabelsOnZoom);
    }, [status.mapStatus, props.selectedSpaces ?? []]);

    const debounceOnResize = debounce(150, () => {
        const autodeskViewerRef = autodeskViewer.current;
        if (autodeskViewerRef.viewer.container) {
            const webGlUtils = WebGlUtils.instance(props.id);

            if (webGlUtils.hasExtensions(WebGlUtils.OES_texture_float_linear, WebGlUtils.WEBGL_compressed_texture_s3tc)) {
                autodeskViewerRef.viewer.resize();
            } else {
                autodeskViewerRef.viewer.fitToView([], undefined, true);

                if (cameraExtensionContext.positionChanged) {
                    autodeskViewerRef.viewer.navigation.setView(
                        Point.ToVector(cameraExtensionContext.position),
                        Point.ToVector(cameraExtensionContext.target)
                    );

                    if (props.rotateDegrees) {
                        CameraUtils.rotate(autodeskViewerRef.viewer, props.rotateDegrees);
                    }
                }
            }
        }
    });

    const handleOnFloorChangedAsync = useCallback(
        async (viewerFloor: IViewerFloor): Promise<void> => {
            if (autodeskContext.current.activeFloorLevel === viewerFloor.floor.level) {
                return;
            }

            const labelManagerRef = labelManagerReference.current;
            const styleManagerRef = styleManagerReference.current;
            const markerManagerRef = markerManagerReference.current!;
            const autodeskContextRef = autodeskContext.current;

            const autodeskViewerRef = autodeskViewer.current;
            await autodeskViewerRef.clearViewAsync();
            if (onBeforeFloorChangeAsync) {
                await onBeforeFloorChangeAsync(viewerFloor.floor);
            }

            styleManagerRef.cleanStyles();
            labelManagerRef.cleanLabels();
            markerManagerRef.cleanMarkers();

            const viewModels = await autodeskViewerRef.openViewAsync(viewerFloor);

            // Set the initial camera position after floorswitch, now the zoom level can be derived at all time
            const cameraPosition = autodeskViewerRef.viewer.getCamera().position;
            markerManagerRef.cameraPosition = cameraPosition;
            labelManagerRef.cameraPosition = cameraPosition;

            markerManagerRef.initDataVisualizationWithMarker();

            autodeskContextRef.model = viewModels[0];
            autodeskContextRef.activeFloorLevel = viewerFloor.floor.level;

            ZoomUtils.setZoomLimit(autodeskViewerRef.viewer);

            if (props.rotateDegrees) {
                CameraUtils.rotate(autodeskViewerRef.viewer, props.rotateDegrees);
            }

            onFloorChangedAsync({ model: viewModels[0], floor: viewerFloor.floor, viewer: autodeskViewerRef.viewer });
        },
        [onFloorChangedAsync, onBeforeFloorChangeAsync, props.rotateDegrees]
    );

    /*
     * Show floor map
     */
    useEffect(() => {
        const autodeskViewerRef = autodeskViewer.current;
        const { viewerFloors } = autodeskContext.current;

        if (status.mapStatus !== MapStatus.floorsLoaded || !viewerFloors || props.startFloorLevel == null) {
            return;
        }

        let resizeObserver: ResizeObserver | undefined = undefined;

        try {
            resizeObserver = new ResizeObserver(() => {
                debounceOnResize();
            });
        } catch {
            // do nothing, ResizeObserver will be replaced by window.onresize later on.
        }

        const getDefaultFloorAsync = async (): Promise<IViewerFloor> => {
            const viewerFloorsList = await Promise.all<IViewerFloor>(viewerFloors.listAsync);

            return viewerFloorsList.reduce((prev, curr) => (prev.floor.level < curr.floor.level ? prev : curr));
        };

        const showFloorAsync = async (): Promise<void> => {
            const currentViewFloorAsync = viewerFloors[props.startFloorLevel];
            let currentViewFloor: IViewerFloor;

            if (currentViewFloorAsync == null) {
                currentViewFloor = await getDefaultFloorAsync();
            } else {
                currentViewFloor = await currentViewFloorAsync;
            }

            const options = {
                autodeskContainerId: props.id,
                viewerFloors: viewerFloors,
                currentFloorLevel: currentViewFloor.floor.level,
                onFloorChangedAsync: handleOnFloorChangedAsync,
                maxNumberOfFloorButtons: props.maxNumberOfFloorButtons,
            };
            floorSelectorExtensionOptionsReference.current = options;
            await extensions.current?.loadFloorExtensionAsync(autodeskViewerRef.viewer, options);

            await handleOnFloorChangedAsync(currentViewFloor);

            if (resizeObserver != null) {
                resizeObserver.observe(viewerReference.current!);
            } else {
                window.onresize = debounceOnResize;
            }

            setStatus({
                mapStatus: MapStatus.floorLoaded,
            });
        };

        showFloorAsync();
    }, [props.id, status.mapStatus, props.startFloorLevel, props.maxNumberOfFloorButtons, handleOnFloorChangedAsync]);

    /*
     * Load a specific floor
     */
    useEffect(() => {
        const autodeskViewerRef = autodeskViewer.current;
        const { viewerFloors, activeFloorLevel } = autodeskContext.current;

        if (
            status.mapStatus !== MapStatus.floorLoaded ||
            !viewerFloors ||
            props.currentFloorLevel == null ||
            activeFloorLevel === null ||
            activeFloorLevel === props.currentFloorLevel
        ) {
            return;
        }

        const updateToolbarAndChangeFloorAsync = async (): Promise<void> => {
            const currentViewFloorAsync = viewerFloors[props.currentFloorLevel!];

            if (currentViewFloorAsync == null) {
                return;
            }

            const currentViewFloor = await currentViewFloorAsync;

            const options = {
                autodeskContainerId: props.id,
                viewerFloors: viewerFloors,
                currentFloorLevel: props.currentFloorLevel!,
                onFloorChangedAsync: handleOnFloorChangedAsync,
                maxNumberOfFloorButtons: props.maxNumberOfFloorButtons,
            };
            floorSelectorExtensionOptionsReference.current = options;
            await extensions.current?.loadFloorExtensionAsync(autodeskViewerRef.viewer, options);

            await handleOnFloorChangedAsync(currentViewFloor);
        };

        updateToolbarAndChangeFloorAsync();
    }, [props.id, props.maxNumberOfFloorButtons, props.currentFloorLevel, handleOnFloorChangedAsync, status.mapStatus]);

    /*
     * Load floors when status is 'venueInitialized' and alters status to 'FloorsLoaded' when floors are loaded
     */
    useDeepCompareEffect(() => {
        if (status.mapStatus !== MapStatus.venueInitialized || !props.floors) {
            return;
        }

        const getViewerFloorsAsync = (viewer: AutodeskViewer, context: AutodeskContext): ViewerFloorsDictionary => {
            let viewerVenue = globalThis.viewerVenue as unknown as IViewerVenue;

            if (viewerVenue && viewerVenue.venueId === context.activeVenueId) {
                return viewerVenue.viewerFloors;
            }

            const viewerFloors = new ViewerFloorsDictionary();

            for (const floor of props.floors!) {
                const viewerFloor = viewer.initModelByDocumentIdAsync(floor, props.onDocumentLoadFailure);
                viewerFloors[floor.level] = viewerFloor;
            }

            if (props.cacheVenueFloors) {
                viewerVenue = {
                    venueId: context.activeVenueId!,
                    viewerFloors: viewerFloors,
                };

                globalThis.viewerVenue = viewerVenue;
            }

            return viewerFloors;
        };

        const handleFloorLoadAsync = async (): Promise<void> => {
            const autodeskViewerRef = autodeskViewer.current;

            extensions.current?.unLoadFloorExtensionAsync(autodeskViewerRef.viewer);

            if (props.floors!.length === 0) {
                styleManagerReference.current.cleanStyles();
                labelManagerReference.current.cleanLabels();
                await autodeskViewer.current.clearViewAsync();
            }

            const autodeskContextRef = autodeskContext.current;

            autodeskContextRef.viewerFloors = getViewerFloorsAsync(autodeskViewerRef, autodeskContextRef);

            markerManagerReference.current = new MarkerManager(autodeskViewer.current);

            setStatus({
                mapStatus: MapStatus.floorsLoaded,
            });
        };

        // load floor
        handleFloorLoadAsync();
    }, [status.mapStatus, props.floors ?? [], props.onDocumentLoadFailure, props.cacheVenueFloors]);

    /*
     * Init click & zoom events
     */
    useEffect(() => {
        if (status.mapStatus < MapStatus.floorLoaded) {
            return;
        }

        const registerZoomEventHandler = (): void => {
            if (onZoomChange != null) {
                zoomManagerReference.current.registerOnCameraChangedEvent(onZoomChange);
            }
        };

        const loadSelectorExtensionAsync = async (viewer: Autodesk.Viewing.GuiViewer3D): Promise<void> => {
            const getSpaceById = (dbId: number): ISpaceOnMap | undefined => {
                if (!spaces) {
                    return undefined;
                }
                return spaces.find((sp) => sp.dbId === dbId);
            };

            await extensions.current?.loadSelectorExtensionAsync(viewer, {
                onClickAsync: onPlaceClickAsync ?? emptyOnPlaceClickAsync,
                getSpaceById: getSpaceById,
                context: selectorExtensionContext,
            });
        };

        const emptyOnPlaceClickAsync = async (eventContext: ISelectorEvent): Promise<void> => { }

        const loadClickEventsExtensionAsync = async (viewer: Autodesk.Viewing.GuiViewer3D): Promise<void> => {
            const clickWrapper = (eventContext: IClickEvent): void => {
                const marker = eventContext.marker;

                // Execute the onClick code that has been given to the marker and exit
                if (marker?.onClick) {
                    marker.onClick(eventContext);
                    return;
                }

                // Execute the code that has been set trough the onClickAsync property
                if (onClickAsync) {
                    onClickAsync(eventContext);
                }
            };

            // Same, but not in use with the BE services at the moment
            const rightClickWrapper = (eventContext: IClickEvent): void => {
                const marker = eventContext.marker;

                if (marker?.onRightClick) {
                    marker.onRightClick(eventContext);
                    return;
                }

                if (onRightClickAsync) {
                    onRightClickAsync(eventContext);
                }
            };

            let spaceMarkers = Array<IMarker>();

            spaces?.forEach(space => {
                if (space.style?.placeMarker) {
                    let theMarker = space.style.placeMarker;
                    theMarker.position = space.position;
                    theMarker.internalDbId = space.dbId; // Give the marker a unique id so it can be matched with the viewer click event.
                    spaceMarkers.push(space.style.placeMarker);
                }
            });

            const allMarkers = nonSpaceMarkers.concat(spaceMarkers);

            if (onClickAsync || onRightClickAsync || allMarkers.some((marker) => marker.onClick != null || marker.onRightClick != null)) {
                extensions.current?.loadClickEventsExtensionAsync(viewer, {
                    markerManager: markerManagerReference.current!,
                    markers: allMarkers,
                    onClick: clickWrapper,
                    onRightClick: rightClickWrapper,
                });
            }
        };

        const loadExtensionsAsync = async (viewer: Autodesk.Viewing.GuiViewer3D): Promise<void> => {
            await extensions.current?.loadContextMenuExtensionAsync(viewer);
            await loadClickEventsExtensionAsync(viewer);
            await loadSelectorExtensionAsync(viewer);
        };

        registerZoomEventHandler();
        loadExtensionsAsync(autodeskViewer.current.viewer);
    }, [status.mapStatus, nonSpaceMarkers, spaces, onClickAsync, onRightClickAsync, onPlaceClickAsync, onZoomChange, selectorExtensionContext]);

    /**
     * Handle Autodesk (proxy) connection initialization
     */
    useEffect(() => {
        if (
            ((status.mapStatus === MapStatus.mapInitialized && props.venueId != null) ||
                (status.mapStatus > MapStatus.mapInitialized && props.venueId !== autodeskContext.current.activeVenueId)) &&
            props.accessToken != null &&
            props.proxyEndpoint != null
        ) {
            const setProxy = (): void => {
                endpointContextBuilder.current
                    .setApiEndpoint(props.proxyEndpoint!)
                    .setAccessToken(props.accessToken!)
                    .setScope(props.venueId!)
                    .setCustomHeaders(props.customHeaders)
                    .finalize();
            };

            setProxy();

            setStatus({ mapStatus: MapStatus.venueInitialized });
            autodeskContext.current.activeVenueId = props.venueId;
            autodeskContext.current.activeFloorLevel = undefined;
            setSpaces(undefined);
        }
    }, [status.mapStatus, props.venueId, props.accessToken, props.proxyEndpoint, props.customHeaders]);

    /* Set access token */
    useEffect(() => {
        if (props.accessToken !== null) {
            const setAccessToken = (): void => {
                endpointContextBuilder.current.setAccessToken(props.accessToken!).finalize();
            };
            setAccessToken();
        }
    }, [props.accessToken]);

    /**
     * Handle (un)mount of the Autodesk Map
     */
    useEffect(() => {
        const currentView = autodeskViewer.current;
        const viewer = viewerReference.current;
        const extensionManager = extensions.current;

        const appendPopupContainerElement = (): void => {
            const popupContainer = document.getElementById(AutodeskConstants.Elements.PopupContainerId);

            if (popupContainer == null) {
                const wrapper = document.createElement("div");
                wrapper.setAttribute("id", AutodeskConstants.Elements.PopupContainerId);
                viewerReference.current!.firstChild?.appendChild(wrapper);
            }
        };

        const handleViewerInit = async (): Promise<void> => {
            await autodeskViewer.current.initAsync(viewerReference.current!);

            appendPopupContainerElement();

            setStatus({ mapStatus: MapStatus.mapInitialized });
        };

        const options: Autodesk.Viewing.InitializerOptions = {
            env: "AutodeskProduction",
            // set access token value to avoid autodesk sdk console errors, token is retrieved using the proxy
            accessToken: "ignore",
            language: "en",
        };

        extensionManager?.register();
        Autodesk.Viewing.Initializer(options, handleViewerInit);

        return (): void => {
            extensionManager?.unLoad(currentView.viewer);
            extensionManager?.unregister();
            currentView.viewer?.finish();
            // do not call Autodesk.Viewing?.shutdown() here, because it will shutdown all instances of this component
            //  if necessary,

            if (viewer) {
                // cleanup autodesk-viewer-container element, otherwise every venue change will result in another autodesk div
                while (viewer.firstChild) {
                    viewer.removeChild(viewer.lastChild!);
                }
            }
        };
    }, []);

    return <div id={props.id} className="autodesk-viewer-container" ref={viewerReference}></div>;
};
